import indexGraphData from 'actions/graphData/index';
import indexThermometer from 'actions/thermometer/index';
import indexTreatments from 'actions/treatment/index';
import Menu from 'components/Layout/Menu/Menu';
import { Displaymode } from 'components/Layout/NavigationBox/NavigationBox';
import WelcomeButton from 'components/Layout/WelcomeButton/WelcomeButton';
import Avatar from 'components/UI/Avatar/Avatar';
import TrophyModal from 'components/UI/Avatar/TrophyGarden/TrophyModal/TrophyModal';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef, useState, useContext } from 'react';
import Confetti from 'react-dom-confetti';
import { useDispatch, useSelector } from 'react-redux';
import ZoomButton from 'screens/Home/components/ZoomButton/ZoomButton';
import { useLocation } from 'react-router';
import { getCurrentSeason } from 'utilities/getCurrentSeason';
import { getTilePosition } from 'utilities/getTilePosition';
import useFunctionAsState from 'utilities/useFunctionAsState';
import { GlobalContext } from 'utilities/GlobalContext';
import SmallScreenWarning from 'screens/Home/components/SmallScreenWarning/SmallScreenWarning';
import Grid from './components/Grid/Grid';
import renderer from './components/renderer';
import ThermometerSequence from './components/ThermometerSequence/ThermometerSequence';
import Tile from './components/Tile/Tile';
import WelcomeSequence from './components/WelcomeSequence';
import './Home.module.scss';
import VideoPopup from './components/VideoPopup/VideoPopup';
import PhoneBlock from './components/PhoneBlock/PhoneBlock';

type CurrentTile = {
    id: number;
    originX: number;
    originY: number;
    ref: React.RefObject<HTMLDivElement>;
};

type ThemeType = 'winter' | 'summer' | 'fall' | 'spring';

type HomeContextTypes = {
    currentTile: CurrentTile;
    setCurrentTile: React.Dispatch<React.SetStateAction<CurrentTile>>;
    gridRef:
        | string
        | ((instance: HTMLDivElement | null) => void)
        | React.RefObject<HTMLDivElement>
        | null
        | undefined;
    panZoomLibrary: any;
    setHandleZoom: React.Dispatch<React.SetStateAction<any>>;
    path: string;
    handleZoom: any;
    welcomeSequence: WelcomeSequenceOptions;
    setWelcomeSequence: React.Dispatch<
        React.SetStateAction<WelcomeSequenceOptions>
    >;
    welcomeDisplayMode: Displaymode;
    setWelcomeDisplayMode: React.Dispatch<React.SetStateAction<Displaymode>>;
    welcomeTop: string;
    setWelcomeTop: React.Dispatch<React.SetStateAction<string>>;
    welcomeLeft: string;
    setWelcomeLeft: React.Dispatch<React.SetStateAction<string>>;
    welcomeRight: string;
    setWelcomeRight: React.Dispatch<React.SetStateAction<string>>;
    welcomeX: string;
    setWelcomeX: React.Dispatch<React.SetStateAction<string>>;
    welcomeY: string;
    setWelcomeY: React.Dispatch<React.SetStateAction<string>>;
    openThermometer: number | string;
    setOpenThermometer: React.Dispatch<React.SetStateAction<number | string>>;
    setOpenVideo: React.Dispatch<React.SetStateAction<number>>;
    theme: ThemeType;
    trophies?: number[];
    selectedTrophy: number;
    setSelectedTrophy: React.Dispatch<React.SetStateAction<number>>;
};

export const HomeContext = React.createContext({} as HomeContextTypes);

export default function Home() {
    const [
        welcomeSequence,
        setWelcomeSequence,
    ] = useState<WelcomeSequenceOptions>('initial');
    const [welcomeDisplayMode, setWelcomeDisplayMode] = useState<Displaymode>(
        'absolute'
    );

    const { setSelected, setMenuY } = useContext(GlobalContext);

    const [welcomeTop, setWelcomeTop] = useState('2em');
    const [welcomeRight, setWelcomeRight] = useState('50%');
    const [welcomeLeft, setWelcomeLeft] = useState('');
    const [welcomeX, setWelcomeX] = useState('50%');
    const [welcomeY, setWelcomeY] = useState('0');
    const [openThermometer, setOpenThermometer] = useState(0);
    const [openVideo, setOpenVideo] = useState<number | null>(null);
    const [triggerConfetti, setTriggerConfetti] = useState(false);
    const location = useLocation();

    const avatarRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const [panZoomLibrary, setPanZoomLibrary] = useState<any>(null);
    const [currentTile, setCurrentTile] = useState({
        id: 0,
        originX: 0,
        originY: 0,
        ref: avatarRef,
    });
    const [handleZoom, setHandleZoom] = useFunctionAsState(null);

    const path = 'path3';
    const dispatch = useDispatch();

    const treatmentsById = useSelector(
        ({ treatment }: { treatment: Treatment }) => treatment.entities.byId
    );

    // because treatments can now be attached multiple times,
    // we map over the treatments inside the user object and enrich that
    // with all the actual data from the Treatment endpoint (since user treatment only contains ID and Pivot)
    const user = useSelector((store: ReduxStore) => store.authentication?.user);
    const treatments = user?.treatments.map((treatment) => ({
        ...treatmentsById[treatment.id],
        pivot: treatment.pivot,
    }));

    const trophies = useSelector((store: ReduxStore) =>
        store.authentication?.user
            ? Object.entries(store.authentication.user.progress.statues)
                  .map((t) => {
                      const id = t[0];
                      const isCompleted = t[1];

                      if (isCompleted) return parseInt(id);
                      return undefined;
                  })
                  .filter((exists) => exists)
            : undefined
    ) as number[] | undefined;

    const [selectedTrophy, setSelectedTrophy] = useState(0);

    const season = getCurrentSeason() || 'spring';
    const theme: ThemeType = season;

    // set current tile id
    useEffect(() => {
        setCurrentTile((prev) => ({
            ...prev,
            id:
                treatments
                    ?.map((t, index) => t?.pivot?.open && index + 1)
                    .find((isTrue) => isTrue) || 0,
        }));
    }, []);

    // move position to avatar on mount
    useEffect(() => {
        const { originX, originY } = getTilePosition(avatarRef);

        return setCurrentTile((prev) => ({
            ...prev,
            originX,
            originY,
        }));
    }, [currentTile.id!!]);

    const trophiesCopy = [...(trophies || [])];
    useEffect(() => {
        // new trophy has been generated
        if (trophiesCopy?.length !== trophies?.length) {
            // find new trophy
            const newTrophy = trophies?.find((t) => !trophiesCopy?.includes(t));
            if (newTrophy) {
                setTriggerConfetti(true);
                setSelectedTrophy(newTrophy);
            }
        }
    }, [trophies?.length]);

    useEffect(() => {
        // check to see if we need to open up the logbook
        if (location.state && location.state.openLogbook) {
            setSelected('calendar');
            setMenuY(348.5); // @ToDo: fix ugly hard coded value
        }
    }, [location]);

    useEffect(() => {
        const inst = renderer({
            scaleSensitivity: 5,
            minScale: 0.3,
            maxScale: 2.5,
            element: gridRef.current,
        });

        setPanZoomLibrary(inst);

        dispatch(indexTreatments());
        dispatch(indexGraphData());
        dispatch(indexThermometer());
    }, []);

    return (
        <HomeContext.Provider
            value={{
                currentTile,
                setCurrentTile,
                gridRef,
                panZoomLibrary,
                setHandleZoom,
                path,
                handleZoom,
                welcomeSequence,
                setWelcomeSequence,
                welcomeDisplayMode,
                setWelcomeDisplayMode,
                welcomeTop,
                setWelcomeTop,
                welcomeRight,
                setWelcomeRight,
                welcomeLeft,
                setWelcomeLeft,
                welcomeX,
                setWelcomeX,
                welcomeY,
                setWelcomeY,
                openThermometer,
                setOpenThermometer,
                setOpenVideo,
                theme,
                trophies,
                selectedTrophy,
                setSelectedTrophy,
            }}
        >
            <SmallScreenWarning />
            <PhoneBlock />
            <div
                styleName="container"
                style={{
                    // @ts-ignore
                    background: '#28e57d',
                }}
            >
                {welcomeSequence !== 'initial' && <WelcomeSequence />}
                <AnimatePresence>
                    {!!openThermometer && <ThermometerSequence />}
                    {openVideo !== null && (
                        <VideoPopup
                            openVideo={openVideo}
                            setOpenVideo={setOpenVideo}
                        />
                    )}
                </AnimatePresence>
                <Grid>
                    <div styleName="avatar" ref={avatarRef}>
                        <Avatar trophyGarden />
                    </div>
                    {treatments
                        ?.sort((a, b) => a.pivot.order - b.pivot.order)
                        .map(
                            (t, index) =>
                                !!t.id && (
                                    <Tile
                                        key={index} // test to check if this resolves issue with lots of the same treatment
                                        id={t.id}
                                        name={t.name}
                                        homework={t.homework}
                                        image={t?.image}
                                        open={t.pivot.open}
                                        finished={t.pivot.finished}
                                        isRepeat={t.is_rehearsal}
                                        number={t.number}
                                        index={index + 1}
                                    />
                                )
                        )}
                </Grid>
                <div styleName="left-menu">
                    <WelcomeButton avatarRef={avatarRef} />
                    {handleZoom && <ZoomButton />}
                </div>
                <Menu />
                {!!selectedTrophy && <TrophyModal />}
                <div styleName="confetti">
                    <Confetti active={triggerConfetti} />
                </div>
            </div>
        </HomeContext.Provider>
    );
}
